import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { ModelType, TrimType } from '../../../types';
import { fetchWithType } from '../../../utils/api';
import { getAdminUrl, getApiToken } from '../../../utils/helpers';
import KIAImage from '../ui/image/KIAImage';
import CarouselBusinessRows from './CarouselBusinessRows';
import CarouselLoanRows from './CarouselLoanRows';
import CarouselOtherRows from './CarouselOtherRows';
import CarouselPrivateRows from './CarouselPrivateRows';
import { getStoreByKey } from './store/CarouselStore';
import { CarouselFieldsEnum, ImageTypeEnum } from './types/CarouselTypes';

interface CarouselCardProps {
  model: ModelType;
  trimCode?: string;
  uniqId: string;
  type?: 'single' | 'carousel' | 'grid';
}

const CarouselCard = ({ model, trimCode, type, uniqId }: CarouselCardProps) => {
  const { options, direction, businessEditionFilter } = useStore(
    getStoreByKey(uniqId),
  );
  const getInitialTrim = () => {
    if (trimCode) return null;
    if (businessEditionFilter === 'only') {
      return (
        model.visible_cars?.find((c) => c.business_edition) ?? model.first_car
      );
    }
    if (businessEditionFilter === 'exclude') {
      return (
        model.visible_cars?.find((c) => !c.business_edition) ?? model.first_car
      );
    }
    return model.first_car;
  };
  const [trim, setTrim] = useState<TrimType>(getInitialTrim());
  const [isMobile, setIsMobile] = useState<boolean>(true);

  useEffect(() => {
    // Fetch trim if trimCode is provided
    if (trimCode) {
      fetchTrim(trimCode);
    }
    const handleResize = () => {
      setIsMobile(window.innerWidth < 700);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getTrimImage = () => {
    switch (options?.imageType) {
      case ImageTypeEnum.EXTERIOR:
        return (
          trim.context_picture ?? model.context_picture ?? trim.image ?? ''
        );
      default:
        return trim.image ?? model.context_picture ?? '';
    }
  };

  const fetchTrim = async (code: string) => {
    try {
      const { data } = await fetchWithType<TrimType>(
        getAdminUrl() + '/api/car/' + code,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${getApiToken()}`,
          },
        },
      );
      if (data && data.id) {
        setTrim(data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleChangeTrim = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedTrim = model.visible_cars.find(
      (car) => car.id === parseInt(e.target.value),
    );
    if (selectedTrim) {
      fetchTrim(selectedTrim.code);
    }
  };

  const getModelName = () => {
    if (!options || options?.fields?.includes(CarouselFieldsEnum.CHANGE_TRIM)) {
      return model.name;
    } else {
      return trim.level ? `${model.name} | ${trim.level.name}` : trim.name;
    }
  };

  return trim && model ? (
    <div
      style={{
        minWidth:
          type === 'grid'
            ? ''
            : options?.imageType !== ImageTypeEnum.EXTERIOR
              ? 'clamp(200px, 80vw, 378px)'
              : 'clamp(200px, 80vw, 456px)',
        maxWidth:
          type === 'grid'
            ? '940px'
            : direction === 'vertical'
              ? '100%'
              : options?.imageType === ImageTypeEnum.EXTERIOR
                ? '456px'
                : '378px',
      }}
      className="@container flex h-full mx-auto w-full"
    >
      <div
        className={`carousel-card  w-full min-h-[340px] group rounded-lg bg-kia-grey m-1 shadow snap-center grid grid-rows-subgrid row-span-12 @xlcard:row-span-1 @xlcard:grid-cols-2`}
      >
        <a
          href={model.url}
          aria-label={`Utforska ${model.name}`}
          className="no-underline"
        >
          <div
            style={
              {
                '--color': model.background_color,
              } as React.CSSProperties
            }
            className="relative overflow-hidden h-full flex items-center"
          >
            <div
              className={`w-full ${direction === 'vertical' && options?.imageType === ImageTypeEnum.EXTERIOR ? 'sm:h-full' : ''} ${!options || options?.imageType !== ImageTypeEnum.EXTERIOR ? 'pt-6' : ''}`}
            >
              <KIAImage
                className={`group-hover:scale-105 transition-transform w-full h-full relative z-[1] ${options?.imageType === ImageTypeEnum.EXTERIOR && (trim.context_picture || model.context_picture) ? 'object-cover aspect-[4/3]' : 'object-contain aspect-[2/1]'}`}
                width={300}
                src={getTrimImage()}
                alt={model.name}
              />
            </div>
            {options?.imageType === ImageTypeEnum.STUDIO_BG && (
              <>
                <svg
                  className={`absolute top-0 left-0 hidden @xlcard:block h-[85%] w-full @xlcard:w-[85%] @xlcard:h-full`}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 100 L100 100 C90 50, 90 50, 100 0 L0 0 Z"
                    fill={model.background_color}
                  />
                </svg>
                <svg
                  className={`absolute top-0 left-0 @xlcard:hidden h-[85%] w-full @xlcard:w-[85%] @xlcard:h-full `}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 100 100"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M100 0 L100 100 C50 90, 50 90, 0 100 L0 0 Z"
                    fill={model.background_color}
                  />
                </svg>
              </>
            )}
            <p
              className={`absolute text-black z-10 bg-white py-1 px-4 shadow-sm text-[13px] rounded-full right-4 top-4 @xlcard:right-auto @xlcard:left-6 @xlcard:top-6`}
            >
              {model.type.name}
            </p>
          </div>
        </a>
        <div
          className={`py-4 px-4 grid grid-rows-subgrid row-span-12 @xlcard:row-span-1 @xlcard:flex @xlcard:flex-col @xlcard:my-auto ${(isMobile || direction === 'horizontal') && options?.imageType !== ImageTypeEnum.EXTERIOR ? 'pt-0' : ''} ${direction === 'vertical' && !isMobile ? (!options ? '@xlcard:py-8' : '@xlcard:py-12') : ''} ${direction === 'vertical' && options?.imageType === ImageTypeEnum.EXTERIOR ? 'sm:!px-8' : ''}${direction === 'vertical' && options?.imageType !== ImageTypeEnum.EXTERIOR ? '@xlcard:pl-0' : ''}`}
        >
          <a
            href={model.url}
            aria-label={`Utforska ${model.name}`}
            className="no-underline mb-4"
          >
            <h3
              className={`${direction === 'vertical' ? 'md:text-lg md:py-2' : ''} group-hover:underline text-lg`}
            >
              {getModelName()}
            </h3>
          </a>
          {options?.fields?.some((field) =>
            [
              CarouselFieldsEnum.PRIVATE_DESCRIPTION,
              CarouselFieldsEnum.COMPANY_DESCRIPTION,
              CarouselFieldsEnum.TRIM_LEVEL_DESCRIPTION,
            ].includes(field),
          ) && (
            <p className="mb-4">
              {options?.fields.includes(CarouselFieldsEnum.PRIVATE_DESCRIPTION)
                ? trim.description
                : options.fields.includes(
                      CarouselFieldsEnum.COMPANY_DESCRIPTION,
                    )
                  ? trim.description_business
                  : options.fields.includes(
                        CarouselFieldsEnum.TRIM_LEVEL_DESCRIPTION,
                      ) && trim.level?.description
                    ? trim.level.description
                    : null}
            </p>
          )}
          {options?.fields?.includes(CarouselFieldsEnum.FEATURES) && (
            <p className="mb-4">{trim.features}</p>
          )}
          {!options ||
          options?.fields?.includes(CarouselFieldsEnum.CHANGE_TRIM) ? (
            <div className="relative mb-4">
              <select
                onChange={(e) => handleChangeTrim(e)}
                className="w-full px-4 py-3 text-sm pr-10 bg-white rounded-full shadow-md"
              >
                {(businessEditionFilter === 'only'
                  ? model.visible_cars.filter((c) => c.business_edition)
                  : businessEditionFilter === 'exclude'
                    ? model.visible_cars.filter((c) => !c.business_edition)
                    : model.visible_cars
                )
                  .filter((c) => !c.show)
                  .map((car) => (
                    <option key={car.id} value={car.id}>
                      {car.level
                        ? `${model.name} | ${car.level.name}`
                        : car.name}
                    </option>
                  ))}
              </select>
              <svg
                className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
                width="12"
                height="8"
                viewBox="0 0 12 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0.833496 1.5L5.8335 6.5L10.8335 1.5"
                  stroke="#05141F"
                  strokeWidth="2"
                ></path>
              </svg>
            </div>
          ) : null}
          <CarouselPrivateRows uniqId={uniqId} trim={trim} model={model} />
          <CarouselLoanRows uniqId={uniqId} trim={trim} model={model} />
          <CarouselBusinessRows uniqId={uniqId} trim={trim} />
          <CarouselOtherRows uniqId={uniqId} trim={trim} model={model} />
          {(model.url && !options) ||
          options?.fields?.includes(CarouselFieldsEnum.CTA_DISCOVER) ? (
            <a
              className="text-[14px] py-2 underline-offset-4 font-semibold flex gap-1 items-center"
              href={model.url}
            >
              <span>Utforska bilen här</span>
            </a>
          ) : null}
        </div>
      </div>
    </div>
  ) : null;
};

export default CarouselCard;
